import * as THREE from 'three';

/**
 * HealSpell is a self-cast visual effect (not a projectile).
 * It creates a rising green particle column around the caster.
 */
export class HealSpell {
  private scene: THREE.Scene;
  private group: THREE.Group;
  private particles!: THREE.Points;
  private ring!: THREE.Mesh;
  private elapsed: number = 0;
  private readonly duration: number = 1.5; // seconds
  private disposed: boolean = false;
  private animationId: number | null = null;

  constructor(scene: THREE.Scene, position: THREE.Vector3) {
    this.scene = scene;
    this.group = new THREE.Group();
    this.group.position.copy(position);

    this.createVisual();
    this.scene.add(this.group);
    this.animate();
  }

  private createVisual(): void {
    // Rising particles column
    const particleCount = 60;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const radius = 0.5 + Math.random() * 0.3;
      positions[i * 3] = Math.cos(angle) * radius;
      positions[i * 3 + 1] = Math.random() * 3;
      positions[i * 3 + 2] = Math.sin(angle) * radius;

      // Green-white gradient
      colors[i * 3] = 0.3 + Math.random() * 0.3;
      colors[i * 3 + 1] = 0.8 + Math.random() * 0.2;
      colors[i * 3 + 2] = 0.3 + Math.random() * 0.3;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.15,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending
    });

    this.particles = new THREE.Points(geometry, material);
    this.group.add(this.particles);

    // Ground ring
    const ringGeometry = new THREE.RingGeometry(0.8, 1.2, 32);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0x44FF44,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide
    });
    this.ring = new THREE.Mesh(ringGeometry, ringMaterial);
    this.ring.rotation.x = -Math.PI / 2;
    this.ring.position.y = 0.05;
    this.group.add(this.ring);

    // Central glow
    const glowGeometry = new THREE.SphereGeometry(0.3, 16, 16);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0x88FF88,
      transparent: true,
      opacity: 0.5
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    glow.position.y = 1.5;
    this.group.add(glow);
  }

  private animate = (): void => {
    if (this.disposed) return;

    this.elapsed += 0.016;
    const progress = this.elapsed / this.duration;

    if (progress >= 1) {
      this.dispose();
      return;
    }

    // Animate particles rising
    if (this.particles && this.particles.geometry.attributes.position) {
      const positions = this.particles.geometry.attributes.position.array as Float32Array;
      const count = positions.length / 3;
      for (let i = 0; i < count; i++) {
        positions[i * 3 + 1] += 0.03; // Rise upward

        // Spiral motion
        const angle = this.elapsed * 3 + i * 0.2;
        const radius = 0.5 + Math.sin(angle) * 0.2;
        positions[i * 3] = Math.cos(angle + i) * radius;
        positions[i * 3 + 2] = Math.sin(angle + i) * radius;

        // Reset particles that rise too high
        if (positions[i * 3 + 1] > 3.5) {
          positions[i * 3 + 1] = 0;
        }
      }
      this.particles.geometry.attributes.position.needsUpdate = true;
    }

    // Expand and fade ring
    if (this.ring) {
      this.ring.scale.setScalar(1 + progress * 0.5);
      (this.ring.material as THREE.MeshBasicMaterial).opacity = 0.6 * (1 - progress);
    }

    // Fade everything out toward the end
    if (progress > 0.7 && this.particles) {
      (this.particles.material as THREE.PointsMaterial).opacity = 0.9 * (1 - (progress - 0.7) / 0.3);
    }

    this.animationId = requestAnimationFrame(this.animate);
  };

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    this.scene.remove(this.group);
    this.group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
      if (child instanceof THREE.Points) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
}
